import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch.js";
import { createCallToAttentionService } from "../services/createCallToAttention.service.js";
import { updateCallToAttentionService } from "../services/updateCallToAttention.service.js";
import { deleteCallToAttentionService } from "../services/deleteCallToAttention.service.js";
import Swal from "sweetalert2";

const initialState = {
  id: null,
  nameCautioned: "",
  identificationCautioned: "",
  reason: "",
  type: "",
  date: "",
  nameResponsible: "",
  status: "",
  observations: null,
  document: null,
  file: null,
};

const buildFormData = (callToAttention) => {
  const formData = new FormData();
  formData.append("nameCautioned", callToAttention.nameCautioned || "");
  formData.append(
    "identificationCautioned",
    callToAttention.identificationCautioned || ""
  );
  formData.append("reason", callToAttention.reason || "");
  formData.append("type", callToAttention.type || "");
  formData.append("date", callToAttention.date || "");
  formData.append("nameResponsible", callToAttention.nameResponsible || "");
  formData.append("status", callToAttention.status || "");
  formData.append("observations", callToAttention.observations || "");

  if (callToAttention.file instanceof File) {
    formData.append("file", callToAttention.file);
  }

  return formData;
};

export const useCallToAttentionCommand = (refresh) => {
  const fetchData = useFetch();
  const [callToAttention, setCallToAttention] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = ({ name, value }) => {
    setCallToAttention((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const setDefaultValues = (defaultValues) => {
    setCallToAttention(
      defaultValues
        ? {
            ...initialState,
            ...defaultValues,
            file: null,
          }
        : initialState
    );
  };

  const createCallToAttention = async () => {
    try {
      setLoading(true);
      const isValid = validations(callToAttention);
      if (!isValid) return;

      await createCallToAttentionService(fetchData)(
        buildFormData(callToAttention)
      );
      setCallToAttention(initialState);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "El llamado de atención fue creado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "¡Error!",
        text: error.message,
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCallToAttention = async () => {
    try {
      const isValid = validations(callToAttention);
      if (!isValid) return;
      setLoading(true);
      await updateCallToAttentionService(fetchData)({
        id: callToAttention.id,
        formData: buildFormData(callToAttention),
      });
      refresh?.();
      setCallToAttention(initialState);
      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "El llamado de atención fue actualizado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "¡Error!",
        text: error.message,
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCallToAttention = async (id) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar este llamado de atención?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;

      setLoading(true);
      await deleteCallToAttentionService(fetchData)(id);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "El llamado de atención fue eliminado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "¡Error!",
        text: error.message,
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const validations = (callToAttention) => {
    const newErrors = {};

    if (!callToAttention.nameCautioned) {
      newErrors.nameCautioned = "El nombre del amonestado es requerido";
    }

    if (!callToAttention.identificationCautioned) {
      newErrors.identificationCautioned =
        "La identificación del amonestado es requerida";
    }

    if (!callToAttention.reason) {
      newErrors.reason = "La razón del llamado de atención es requerida";
    }

    if (!callToAttention.type) {
      newErrors.type = "El tipo de llamado de atención es requerido";
    }

    if (!callToAttention.date) {
      newErrors.date = "La fecha del llamado de atención es requerida";
    }

    if (!callToAttention.nameResponsible) {
      newErrors.nameResponsible =
        "El nombre del responsable del llamado de atención es requerido";
    }

    if (!callToAttention.status) {
      newErrors.status = "El estado del llamado de atención es requerido";
    }

    if (
      callToAttention.file &&
      callToAttention.file.type !== "application/pdf"
    ) {
      newErrors.file = "El archivo debe ser un PDF";
    }

    const hasErrors = Object.keys(newErrors).length > 0;
    setErrors(newErrors);
    return !hasErrors;
  };

  return {
    callToAttention,
    handleChange,
    setDefaultValues,
    createCallToAttention,
    updateCallToAttention,
    deleteCallToAttention,
    loading,
    errors,
    validations,
  };
};
